import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface BarcodeScannerProps {
  visible: boolean;
  onClose: () => void;
  /** Called when a barcode is successfully scanned or manually entered */
  onScanned: (barcode: string) => void;
  title?: string;
}

/**
 * Barcode scanner modal.
 * - On native: uses expo-camera's built-in barcode scanning via CameraView
 * - On web: falls back to a manual text input
 */
export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  visible,
  onClose,
  onScanned,
  title = 'Scan Barcode',
}) => {
  const styles = useThemedStyles(getStyles);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [scanned, setScanned] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [showManual, setShowManual] = useState(Platform.OS === 'web');

  useEffect(() => {
    if (!visible) {
      setScanned(false);
      setManualInput('');
      setShowManual(Platform.OS === 'web');
      return;
    }
    if (Platform.OS !== 'web') {
      requestPermission();
    }
  }, [visible]);

  const requestPermission = async () => {
    try {
      const { Camera } = await import('expo-camera');
      const { status } = await Camera.requestCameraPermissionsAsync();
      setHasPermission(status === 'granted');
    } catch {
      setHasPermission(false);
      setShowManual(true);
    }
  };

  const handleBarcodeScanned = ({ data }: { data: string }) => {
    if (scanned) return;
    setScanned(true);
    onScanned(data);
    onClose();
  };

  const handleManualSubmit = () => {
    const trimmed = manualInput.trim();
    if (!trimmed) return;
    onScanned(trimmed);
    onClose();
  };

  const renderCamera = () => {
    if (Platform.OS === 'web' || showManual) {
      return (
        <View style={styles.manualContainer}>
          <Ionicons name="barcode-outline" size={64} color={colors.primary} style={styles.barcodeIcon} />
          <Text style={styles.manualLabel}>Enter barcode manually</Text>
          <TextInput
            style={styles.manualInput}
            value={manualInput}
            onChangeText={setManualInput}
            placeholder="e.g. 1234567890123"
            placeholderTextColor={colors.textLight}
            keyboardType="number-pad"
            autoFocus
            onSubmitEditing={handleManualSubmit}
            returnKeyType="done"
          />
          <TouchableOpacity
            style={[styles.submitButton, !manualInput.trim() && styles.submitButtonDisabled]}
            onPress={handleManualSubmit}
            disabled={!manualInput.trim()}
          >
            <Text style={styles.submitButtonText}>Use Barcode</Text>
          </TouchableOpacity>
          {Platform.OS !== 'web' && (
            <TouchableOpacity
              style={styles.switchButton}
              onPress={() => setShowManual(false)}
            >
              <Ionicons name="camera-outline" size={18} color={colors.primary} />
              <Text style={styles.switchButtonText}>Use Camera</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    if (hasPermission === null) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Requesting camera permission...</Text>
        </View>
      );
    }

    if (hasPermission === false) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="camera-off-outline" size={64} color={colors.error} />
          <Text style={styles.errorText}>Camera permission denied</Text>
          <TouchableOpacity
            style={styles.switchButton}
            onPress={() => setShowManual(true)}
          >
            <Ionicons name="pencil-outline" size={18} color={colors.primary} />
            <Text style={styles.switchButtonText}>Enter manually</Text>
          </TouchableOpacity>
        </View>
      );
    }

    // Native camera with barcode scanning
    return (
      <NativeCameraView
        onBarcodeScanned={scanned ? undefined : handleBarcodeScanned}
        onManual={() => setShowManual(true)}
      />
    );
  };

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={24} color={colors.textDark} />
          </TouchableOpacity>
          <Text style={styles.title}>{title}</Text>
          {!showManual && Platform.OS !== 'web' && (
            <TouchableOpacity style={styles.manualButton} onPress={() => setShowManual(true)}>
              <Text style={styles.manualButtonText}>Manual</Text>
            </TouchableOpacity>
          )}
          {showManual && Platform.OS !== 'web' && (
            <View style={styles.manualButton} />
          )}
          {Platform.OS === 'web' && <View style={styles.manualButton} />}
        </View>

        {renderCamera()}
      </View>
    </Modal>
  );
};

/** Separate component so expo-camera import is lazy and won't break web */
const NativeCameraView: React.FC<{
  onBarcodeScanned?: (result: { data: string }) => void;
  onManual: () => void;
}> = ({ onBarcodeScanned, onManual }) => {
  const styles = useThemedStyles(getStyles);
  const [CameraView, setCameraView] = useState<any>(null);

  useEffect(() => {
    import('expo-camera').then(mod => {
      setCameraView(() => mod.CameraView);
    });
  }, []);

  if (!CameraView) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.cameraContainer}>
      <CameraView
        style={styles.camera}
        facing="back"
        barcodeScannerSettings={{ barcodeTypes: ['ean13', 'ean8', 'qr', 'code128', 'code39', 'upc_a', 'upc_e', 'itf14', 'datamatrix'] }}
        onBarcodeScanned={onBarcodeScanned}
      >
        {/* Scanning overlay */}
        <View style={styles.overlay}>
          <View style={styles.scanArea}>
            {/* Corner markers */}
            <View style={[styles.corner, styles.cornerTL]} />
            <View style={[styles.corner, styles.cornerTR]} />
            <View style={[styles.corner, styles.cornerBL]} />
            <View style={[styles.corner, styles.cornerBR]} />
          </View>
          <Text style={styles.scanHint}>Align barcode within the frame</Text>
          <TouchableOpacity style={styles.manualFallback} onPress={onManual}>
            <Ionicons name="pencil-outline" size={16} color={colors.white} />
            <Text style={styles.manualFallbackText}>Enter manually</Text>
          </TouchableOpacity>
        </View>
      </CameraView>
    </View>
  );
};

const SCAN_AREA = 260;

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.xl,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  closeButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  manualButton: {
    width: 64,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  manualButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  cameraContainer: {
    flex: 1,
  },
  camera: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
  },
  scanArea: {
    width: SCAN_AREA,
    height: SCAN_AREA / 2,
    backgroundColor: 'transparent',
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 28,
    height: 28,
    borderColor: colors.white,
  },
  cornerTL: { top: 0, left: 0, borderTopWidth: 3, borderLeftWidth: 3 },
  cornerTR: { top: 0, right: 0, borderTopWidth: 3, borderRightWidth: 3 },
  cornerBL: { bottom: 0, left: 0, borderBottomWidth: 3, borderLeftWidth: 3 },
  cornerBR: { bottom: 0, right: 0, borderBottomWidth: 3, borderRightWidth: 3 },
  scanHint: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  manualFallback: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  manualFallbackText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.xl,
  },
  centerText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.error,
    fontFamily: typography.fontFamily.medium,
    textAlign: 'center',
  },
  manualContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    gap: spacing.md,
  },
  barcodeIcon: {
    marginBottom: spacing.sm,
  },
  manualLabel: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  manualInput: {
    width: '100%',
    maxWidth: 320,
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    textAlign: 'center',
    backgroundColor: colors.background.surface,
  },
  submitButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    marginTop: spacing.xs,
  },
  submitButtonDisabled: {
    opacity: 0.4,
  },
  submitButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
  switchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  switchButtonText: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
});
